// Trips: the list, dispatching a truck (to its loading point, before the load is known), and a trip's
// own page - loading it, delivering it, the invoice (Accounts), and what it earned and cost.
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Page, Card, Btn, Table, Tr, Td, Field, Loading, Totals } from "@/components/kit";
import api from "@/lib/api";
import { act, DateIn, Files, input, k0, n, Num, opts, Select, State, useCan, useLoad, useRegisters } from "@/components/erp";
import { fmtDate, today } from "@/lib/money";

const REGS = ["trucks", "trailers", "drivers", "lanes", "contracts"];

export function Trips() {
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState({ status: "", truck_id: "" });
  const { data, reload } = useLoad("/trips", { status: f.status || undefined, truck_id: f.truck_id || undefined });
  const regs = useRegisters(REGS);
  const showNew = params.get("new") === "1" && can("trips.manage");
  return (
    <Page title="Trips" actions={can("trips.manage") && !showNew && <Btn kind="primary" onClick={() => setParams({ new: "1" })}>+ Dispatch truck</Btn>}>
      {showNew && <Dispatch regs={regs} done={() => { setParams({}); reload(); }} />}
      <div className="mt-4 grid max-w-xl grid-cols-2 gap-3">
        <Field label="Status"><Select value={f.status} onChange={(v) => setF({ ...f, status: v || "" })} empty="All"
          options={["dispatched", "loaded", "delivered", "invoiced", "cancelled"].map((s) => ({ value: s, label: s[0].toUpperCase() + s.slice(1) }))} /></Field>
        <Field label="Truck"><Select value={f.truck_id} onChange={(v) => setF({ ...f, truck_id: v || "" })} empty="All" options={opts(regs.trucks, "plate")} /></Field>
      </div>
      {!data ? <Loading /> : (
        <Table minWidth={900} cols={[{ label: "Trip" }, { label: "Date" }, { label: "Truck" }, { label: "Driver" }, { label: "Route / contract" }, { label: "Status" },
          { label: "Earned", align: "right" }, { label: "Fuel", align: "right" }, { label: "Other", align: "right" }, { label: "Profit", align: "right" }]}>
          {data.map((t) => (
            <Tr key={t.id}>
              <Td><Link to={`/trips/${t.id}`} className="font-medium text-sky-700 hover:underline">{t.no}</Link></Td>
              <Td>{fmtDate(t.load_date)}</Td><Td>{t.truck}</Td><Td>{t.driver}</Td>
              <Td>{t.route || "—"}{t.contract !== "?" ? <div className="text-xs text-slate-500">{t.contract}</div> : null}</Td>
              <Td><State s={t.status} />{t.over_advanced > 0 && <div className="text-xs text-rose-700">over-advanced {k0(t.over_advanced)}</div>}</Td>
              <Td right>{k0(t.net_kes)}</Td><Td right>{k0(t.fuel_cost)}</Td><Td right>{k0(t.other_cost)}</Td>
              <Td right className={t.profit < 0 ? "text-rose-700" : ""}>{k0(t.profit)}</Td>
            </Tr>
          ))}
          {data.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-400">No trips</td></tr>}
        </Table>
      )}
    </Page>
  );
}

function Dispatch({ regs, done }) {
  const [t, setT] = useState({ load_date: today(), origin: "Mombasa" });
  const set = (k) => (v) => setT({ ...t, [k]: v });
  const truck = (regs.trucks || []).find((x) => x.id === t.truck_id);
  const lane = (regs.lanes || []).find((x) => x.id === t.intended_lane_id);
  return (
    <Card title="Dispatch a truck to load" className="mt-4 max-w-3xl bg-white">
      <p className="mt-1 text-sm text-slate-600">The trip opens now; the contract and tonnes are added when it loads.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Truck"><Select value={t.truck_id} onChange={(v) => {
          const tr = (regs.trucks || []).find((x) => x.id === v);
          setT({ ...t, truck_id: v, trailer_id: t.trailer_id || tr?.trailer_id, driver_id: t.driver_id || tr?.driver_id });
        }} options={opts(regs.trucks, "plate")} /></Field>
        <Field label="Trailer"><Select value={t.trailer_id} onChange={set("trailer_id")} options={opts(regs.trailers, "plate")} /></Field>
        <Field label="Driver"><Select value={t.driver_id} onChange={set("driver_id")} options={opts(regs.drivers)} /></Field>
        <Field label="Sent to load for (route)"><Select value={t.intended_lane_id} onChange={set("intended_lane_id")} options={opts(regs.lanes)} /></Field>
        <Field label="Loading point"><input className={input} value={t.origin} onChange={(e) => set("origin")(e.target.value)} /></Field>
        <Field label="Date"><DateIn value={t.load_date} onChange={set("load_date")} /></Field>
      </div>
      {lane && <p className="mt-3 text-sm text-slate-600">Positioning mileage without approval: <b>{k0(lane.positioning_amount)}</b> · route total {k0(lane.mileage_allowance)}</p>}
      <div className="mt-4 flex gap-2">
        <Btn kind="primary" disabled={!truck} onClick={async () => { if (await act(() => api.post("/trips", { ...t, status: "dispatched" }), "Truck dispatched")) done(); }}>Dispatch</Btn>
        <Btn onClick={done}>Cancel</Btn>
      </div>
    </Card>
  );
}

export function TripDetail() {
  const { id } = useParams();
  const can = useCan();
  const navigate = useNavigate();
  const { data: t, reload } = useLoad(`/trips/${id}`);
  const regs = useRegisters(REGS);
  if (!t) return <Page title="Trip"><Loading /></Page>;
  const save = async (body, msg) => { if (await act(() => api.patch(`/trips/${id}`, body), msg)) reload(); };
  const contract = (regs.contracts || []).find((c) => c.id === t.contract_id);
  return (
    <Page title={`Trip ${t.no}`} back={{ to: "/trips", label: "Trips" }} actions={<>
      {can("float.request") && ["dispatched", "loaded"].includes(t.status) &&
        <Btn kind="primary" onClick={() => navigate(`/spend?new=1&kind=advance&trip=${id}`)}>Request mileage</Btn>}
      {can("trips.manage") && !["invoiced", "cancelled"].includes(t.status) &&
        <Btn onClick={() => window.confirm("Cancel this trip?") && save({ status: "cancelled" }, "Trip cancelled")}>Cancel trip</Btn>}
    </>}>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-600">
        <State s={t.status} /><span>{t.truck}{t.trailer ? ` + ${t.trailer}` : ""}</span><span>{t.driver}</span>
        <span>{t.route || "no route yet"}</span><span>{t.contract !== "?" ? t.contract : ""}</span><span>{fmtDate(t.load_date)}</span>
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_380px]">
        <div className="space-y-5">
          {can("trips.manage") && t.status === "dispatched" && <LoadForm t={t} regs={regs} save={save} />}
          {can("trips.manage") && t.status === "loaded" && <DeliverForm t={t} save={save} />}
          {can("trips.invoice") && t.status === "delivered" && <InvoiceForm t={t} usd={contract?.currency === "USD"} save={save} />}
          <Card title="Fuel" className="bg-white">
            <Table minWidth={560} cols={[{ label: "Date" }, { label: "Where" }, { label: "Litres", align: "right" }, { label: "Cost", align: "right" }, { label: "Odometer", align: "right" }, { label: "" }]}>
              {t.fuel_lines.map((f) => <Tr key={f.id}><Td>{fmtDate(f.date)}</Td><Td>{f.source === "outside" ? f.station : "Depot"}</Td><Td right>{k0(f.litres)}</Td>
                <Td right>{k0(f.amount)}</Td><Td right>{k0(f.odometer)}</Td><Td><State s={f.status} />{f.flags?.length ? <div className="text-xs text-rose-700">{f.flags.join("; ")}</div> : null}</Td></Tr>)}
            </Table>
          </Card>
          <Card title="Advances and expenses" className="bg-white">
            <Table minWidth={560} cols={[{ label: "Date" }, { label: "For" }, { label: "Amount", align: "right" }, { label: "" }]}>
              {t.expense_lines.map((e) => <Tr key={e.id}><Td>{fmtDate(e.date)}</Td><Td>{e.category}{e.description ? ` · ${e.description}` : ""} <Files ids={e.attachments} /></Td>
                <Td right>{k0(e.amount)}</Td><Td><State s={e.status} /></Td></Tr>)}
            </Table>
          </Card>
        </div>
        <Card title="Money" className="h-fit bg-white">
          <div className="mt-3"><Totals rows={[
            ["Billed", k0(t.gross)], ["Shortage deducted", k0(t.shortage)], [`Net ${t.currency !== "KES" ? `(${t.currency} at ${t.fx_rate || "?"})` : ""}`, k0(t.net_kes)],
            ["Fuel", k0(t.fuel_cost)], ["Other costs", k0(t.other_cost)], ["Profit", k0(t.profit), t.profit < 0 ? "text-rose-700" : ""]]} /></div>
          <div className="mt-5 text-sm">
            <div className="flex justify-between"><span>Mileage paid</span><span className="tabular-nums">{k0(t.mileage_paid)}</span></div>
            {t.mileage_due != null && <div className="flex justify-between"><span>Route allows</span><span className="tabular-nums">{k0(t.mileage_due)}</span></div>}
            {t.over_advanced > 0 && <div className="mt-1 font-semibold text-rose-700">Over-advanced by {k0(t.over_advanced)} — recover from the driver</div>}
            {t.km > 0 && <div className="flex justify-between"><span>Km ({t.km_source === "route" ? "from the route" : "odometer"}) / L per 100 km</span><span>{k0(t.km)} / {t.l_per_100km ?? "—"}</span></div>}
            <div className="flex justify-between"><span>Tonnes loaded / delivered</span><span>{t.loaded_t ?? "—"} / {t.delivered_t ?? "—"}</span></div>
          </div>
        </Card>
      </div>
    </Page>
  );
}

function LoadForm({ t, regs, save }) {
  const [f, setF] = useState({ contract_id: t.contract_id, loaded_t: t.loaded_t, load_doc: t.load_doc, odometer_start: t.odometer_start });
  return (
    <Card title="It has loaded" className="bg-white">
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Contract"><Select value={f.contract_id} onChange={(v) => setF({ ...f, contract_id: v })} options={opts(regs.contracts)} /></Field>
        <Field label="Tonnes loaded"><Num value={f.loaded_t} onChange={(v) => setF({ ...f, loaded_t: v })} /></Field>
        <Field label="Loading document (STO / delivery no.)"><input className={input} value={f.load_doc || ""} onChange={(e) => setF({ ...f, load_doc: e.target.value })} /></Field>
        <Field label="Odometer at loading"><Num value={f.odometer_start} onChange={(v) => setF({ ...f, odometer_start: v })} /></Field>
      </div>
      <Btn kind="primary" className="mt-4" disabled={!f.contract_id} onClick={() => save({ ...f, loaded_t: n(f.loaded_t), odometer_start: n(f.odometer_start), status: "loaded" }, "Marked loaded")}>Mark loaded</Btn>
    </Card>
  );
}

function DeliverForm({ t, save }) {
  const [f, setF] = useState({ delivered_t: t.delivered_t ?? t.loaded_t, delivery_date: today(), odometer_end: t.odometer_end, shortage_amount: t.shortage_amount });
  return (
    <Card title="It has delivered" className="bg-white">
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Tonnes delivered"><Num value={f.delivered_t} onChange={(v) => setF({ ...f, delivered_t: v })} /></Field>
        <Field label="Delivery date"><DateIn value={f.delivery_date} onChange={(v) => setF({ ...f, delivery_date: v })} /></Field>
        <Field label="Odometer at delivery"><Num value={f.odometer_end} onChange={(v) => setF({ ...f, odometer_end: v })} /></Field>
        <Field label="Shortage the customer deducts (leave empty to work it out)"><Num value={f.shortage_amount} onChange={(v) => setF({ ...f, shortage_amount: v })} /></Field>
      </div>
      <Btn kind="primary" className="mt-4" onClick={() => save({ delivered_t: n(f.delivered_t), delivery_date: f.delivery_date, odometer_end: n(f.odometer_end),
        shortage_amount: n(f.shortage_amount), status: "delivered" }, "Marked delivered")}>Mark delivered</Btn>
    </Card>
  );
}

function InvoiceForm({ t, usd, save }) {
  const [f, setF] = useState({ invoice_no: t.invoice_no || "", invoice_date: today(), fx_rate: t.fx_rate });
  return (
    <Card title="Invoice (raised in Tally)" className="bg-white">
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Field label="Tally invoice no."><input className={input} value={f.invoice_no} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} /></Field>
        <Field label="Invoice date"><DateIn value={f.invoice_date} onChange={(v) => setF({ ...f, invoice_date: v })} /></Field>
        {usd && <Field label="KES per USD on the invoice"><Num value={f.fx_rate} onChange={(v) => setF({ ...f, fx_rate: v })} /></Field>}
      </div>
      <Btn kind="primary" className="mt-4" disabled={!f.invoice_no.trim()} onClick={() => save({ ...f, fx_rate: n(f.fx_rate), status: "invoiced" }, "Invoice recorded")}>Record invoice</Btn>
    </Card>
  );
}
